import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'

import { useQuery } from '@tanstack/react-query'
import { InvoicingAddressesEndpoints } from 'constants/API'
import { Country } from 'constants/country'
import { useAPI } from 'utils/API'

export enum VatValidationStatus {
  VALID = 'VALID',
  INVALID = 'INVALID',
  NOT_VALIDATED = 'NOT_VALIDATED',
}

/**
 * Data Transfer Object representing a single invoicing address.
 *
 * @remarks
 * This structure represents the exact shape returned by the API.
 * Used by queries and components displaying invoicing addresses.
 */
export interface InvoicingAddressDTO {
  /** Unique identifier of the invoicing address */
  id: string

  /** Custom name given to this address by the user */
  addressName: string

  /** Company or individual owner of the address */
  companyOrIndividualName: string

  /** Street name and building number */
  streetAndNumber: string

  /** Apartment / suite number (optional) */
  apartmentNumber?: string

  /** Region, province, or state (optional) */
  region?: string

  /** Postal or ZIP code */
  postalCode: string

  /** City name */
  city: string

  /** Country code */
  country: Country

  /** VAT identification number (optional) */
  vatNumber?: string

  /** Formatted address string (read-only from backend) */
  addressString?: string

  /** VIES validation status of the VAT number (read-only from backend) */
  validationStatus?: VatValidationStatus
}

/* ============================================================
   QUERIES
   ============================================================ */

/**
 * Fetch invoicing addresses belonging to a specific user.
 *
 * @param userId - Identifier of the user whose addresses should be retrieved
 * @param enabled - Controls whether the query should be executed
 *
 * @remarks
 * The query is automatically disabled when `userId` is undefined.
 * Commonly used in admin or impersonation flows.
 *
 * @returns React Query hook with a list of {@link InvoicingAddressDTO}
 */
export function useGetInvoicingAddressesByUserId(userId: string | undefined, enabled: boolean = true) {
  const api = useAPI<InvoicingAddressesEndpoints>()

  return useQuery<InvoicingAddressDTO[]>({
    queryKey: [EntityKeys.INVOICING_ADDRESS, QueryType.LIST, userId],
    queryFn: () => api.get<InvoicingAddressDTO[]>(
      InvoicingAddressesEndpoints.GET_LIST_BY_USER_ID,
      { userId: userId! },
      false
    ),
    enabled: !!userId && enabled
  })
}

/**
 * Fetch a single invoicing address by ID.
 *
 * @param addressId - Identifier of the address to retrieve
 *
 * @returns React Query hook with data, loading and error state
 *
 * @example
 * ```ts
 * const { data, isLoading } = useGetInvoicingAddress('abc123')
 * ```
 */
export function useGetInvoicingAddress(addressId: string) {
  const api = useAPI<InvoicingAddressesEndpoints>()

  return useQuery<InvoicingAddressDTO>({
    queryKey: [EntityKeys.INVOICING_ADDRESS, QueryType.GET_ONE, addressId],
    queryFn: () => api.get<InvoicingAddressDTO>(
      InvoicingAddressesEndpoints.GET_ONE,
      { addressId },
      false
    )
  })
}

/**
 * Fetch the list of all invoicing addresses belonging to the current user.
 *
 * @param enabled - Controls whether the query should be executed
 *
 * @returns React Query hook with a list of {@link InvoicingAddressDTO}.
 *
 * @example
 * ```ts
 * const { data } = useGetInvoicingAddressesList()
 * ```
 */
export function useGetInvoicingAddressesList(enabled: boolean = true) {
  const api = useAPI<InvoicingAddressesEndpoints>()

  return useQuery<InvoicingAddressDTO[]>({
    queryKey: [EntityKeys.INVOICING_ADDRESS, QueryType.LIST_MINE],
    queryFn: () => api.get<InvoicingAddressDTO[]>(
      InvoicingAddressesEndpoints.GET_ALL,
      {},
      false
    ),
    enabled,
  })
}

/* ============================================================
   MUTATIONS
   ============================================================ */

/**
 * Hook used to delete a specific invoicing address.
 *
 * @remarks
 * Automatically invalidates:
 * - Single address query
 * - Address list query
 *
 * @returns React Query mutation with delete functionality
 *
 * @example
 * ```ts
 * const deleteMutation = useDeleteInvoicingAddress()
 * deleteMutation.mutate({ addressId: 'abc123' })
 * ```
 */
export function useDeleteInvoicingAddress() {
  const api = useAPI<InvoicingAddressesEndpoints>()

  return getMutation<
    InvoicingAddressDTO,
    { addressId: string }
  >(
    ({ addressId }) => api.delete(
      InvoicingAddressesEndpoints.DELETE,
      { addressId },
      false
    ),
    (client, { addressId }) => {
      client.invalidateQueries({
        queryKey: [EntityKeys.INVOICING_ADDRESS, QueryType.GET_ONE, addressId]
      })
      client.invalidateQueries({
        queryKey: [EntityKeys.INVOICING_ADDRESS, QueryType.LIST_MINE]
      })
    }
  )
}

/**
 * Payload used when creating or updating an invoicing address.
 *
 * @remarks
 * This is the format expected by the API.
 * Same structure is shared between create and update operations.
 */
export interface InvoicingAddressPayload {
  addressName: string
  companyOrIndividualName: string
  streetAndNumber: string
  apartmentNumber: string
  region: string
  postalCode: string
  city: string
  country: Country
  vatNumber?: string
}

/**
 * Hook used to update an existing invoicing address.
 *
 * @remarks
 * Requires `addressId` in addition to the payload fields.
 * Automatically invalidates:
 * - Single address query
 * - Address list query
 *
 * @returns React Query mutation hook
 *
 * @example
 * ```ts
 * const updateMutation = useUpdateInvoicingAddress()
 *
 * updateMutation.mutate({
 *   addressId: 'abc123',
 *   addressName: 'Office',
 *   ...
 * })
 * ```
 */
export function useUpdateInvoicingAddress() {
  const api = useAPI<InvoicingAddressesEndpoints>()

  return getMutation<
    InvoicingAddressDTO,
    InvoicingAddressPayload & { addressId: string }
  >(
    ({ addressId, ...payload }) => api.put(
      InvoicingAddressesEndpoints.UPDATE,
      { addressId },
      payload,
      false
    ),
    (client, { addressId }) => {
      client.invalidateQueries({
        queryKey: [EntityKeys.INVOICING_ADDRESS, QueryType.GET_ONE, addressId]
      })
      client.invalidateQueries({
        queryKey: [EntityKeys.INVOICING_ADDRESS, QueryType.LIST_MINE]
      })
    }
  )
}

/**
 * Hook used to create a new invoicing address.
 *
 * @remarks
 * Automatically invalidates the address list query after success.
 *
 * @returns React Query mutation hook
 *
 * @example
 * ```ts
 * const createMutation = useCreateInvoicingAddress()
 *
 * createMutation.mutate({
 *   addressName: 'Warehouse',
 *   ...
 * })
 * ```
 */
export function useCreateInvoicingAddress() {
  const api = useAPI<InvoicingAddressesEndpoints>()

  return getMutation<
    InvoicingAddressDTO,
    InvoicingAddressPayload
  >(
    ({ ...payload }) => api.post(
      InvoicingAddressesEndpoints.CREATE,
      {},
      payload,
      false
    ),
    (client) =>
      client.invalidateQueries({
        queryKey: [EntityKeys.INVOICING_ADDRESS, QueryType.LIST_MINE]
      })
  )
}

interface CreateInvoicingAddressForUserPayload extends InvoicingAddressPayload {
  userId: string
}

/**
 * Hook used to create a new invoicing address for specific userId.
 * Admin only.
 *
 * @remarks
 * Automatically invalidates the address list query after success.
 * Automatically invalidates the address list query of specified userId after success.
 *
 * @returns React Query mutation hook
 *
 * @example
 * ```ts
 * const createMutation = useCreateInvoicingAddress()
 *
 * createMutation.mutate({
 *   addressName: 'Warehouse',
 *   ...
 * })
 * ```
 */
export function useCreateInvoicingAddressForUser() {
  const api = useAPI<InvoicingAddressesEndpoints>()

  return getMutation<
    InvoicingAddressDTO,
    CreateInvoicingAddressForUserPayload
  >(
    ({ userId, ...payload }) => api.post(
      InvoicingAddressesEndpoints.CREATE_FOR_USER_ID,
      { userId },
      payload,
      false
    ),
    (client, { userId }) => {
      client.invalidateQueries({
        queryKey: [EntityKeys.INVOICING_ADDRESS, QueryType.LIST_MINE]
      })

      client.invalidateQueries({
        queryKey: [EntityKeys.INVOICING_ADDRESS, QueryType.LIST, userId]
      })
    }
  )
}
